import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import {
  Plane, Activity, Clock, Calendar, CheckCircle, AlertTriangle,
} from "lucide-react";
import { API } from "../../../App";
import {
  QUAL_LABELS, STATUS_META, TONE_CLS,
  fmtDate, todayStr, minutesToHHmmInput,
} from "./diUtils";

// ── Shared sub-components ──────────────────────────────────────────────────

export function ProgressBar({ label, value, target, suffix, ok, mode }) {
  const pct = Math.min(100, target > 0 ? (value / target) * 100 : 0);
  const barColor = ok ? "bg-emerald-500" : pct >= 80 ? "bg-amber-500" : "bg-signal-500";
  const fmt = mode === "minutes" ? minutesToHHmmInput : (v) => v;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1 gap-2">
        <span className="truncate">{label}</span>
        <span className="font-mono whitespace-nowrap">
          <span className={ok ? "text-emerald-300" : "text-slate-200"}>{fmt(value)}</span>
          {suffix && <span>{suffix}</span>}{" "}
          <span className="opacity-60">/ {fmt(target)}{suffix || ""}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function SmallStat({ icon, label, value, accent, tooltip }) {
  const accentCls =
    accent === "ok" ? "text-emerald-300" : accent === "bad" ? "text-red-300" : accent === "warn" ? "text-amber-300" : "text-slate-200";
  return (
    <div className="px-3 py-2.5 rounded border border-cockpit-border bg-slate-900/40 flex flex-col gap-2 min-h-[68px]" title={tooltip || undefined}>
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider leading-none">
        <span className="w-3 h-3 shrink-0 flex items-center justify-center [&_svg]:w-3 [&_svg]:h-3">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className={`font-mono text-lg leading-none mt-auto ${accentCls}`}>{value}</div>
    </div>
  );
}

// Mosaico de uma condição, com a mesma linguagem do cartão do instrutor (5.1): só ganha cor o que falha ou está a acabar.
export function CritTile({ ok, label, sub, days, warn }) {
  const cls = !ok
    ? "border-red-500/40 bg-red-500/5"
    : warn
      ? "border-amber-500/40 bg-amber-500/5"
      : "border-slate-700/60 bg-slate-900/40";
  const icon = !ok
    ? <AlertTriangle className="w-4 h-4 text-red-400" />
    : warn
      ? <AlertTriangle className="w-4 h-4 text-amber-400" />
      : <CheckCircle className="w-4 h-4 text-emerald-400" />;
  return (
    <div className={`p-3 rounded border ${cls}`}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xs font-semibold text-slate-200">{label}</span>
      </div>
      <div className={`text-[11px] mt-1 ${warn ? "text-amber-300/80" : "text-slate-400"}`}>{sub}</div>
      {typeof days === "number" && <div className={`text-[10px] mt-0.5 ${days <= 30 ? "text-amber-300" : "text-slate-500"}`}>{days >= 0 ? `${days} dias até validade` : `Expirado há ${-days} dias`}</div>}
    </div>
  );
}

export function UniformizacaoCard({ uniform }) {
  if (!uniform) return null;
  // never: sem voo registado (âmbar); ok: em dia; amber: perto do prazo; expired: expirada
  const state = {
    never:   { ok: true,  warn: true,  sub: "Sem voo registado" },
    ok:      { ok: true,  warn: false, sub: "Em dia" },
    amber:   { ok: true,  warn: true,  sub: "Perto do prazo" },
    expired: { ok: false, warn: false, sub: "Expirada" },
  }[uniform.status] || { ok: true, warn: true, sub: "Sem voo registado" };
  return (
    <div data-testid="di-uniformizacao-card">
      <CritTile
        ok={state.ok}
        warn={state.warn}
        label="Uniformização anual"
        sub={uniform.last_at ? `${state.sub} · próxima ${uniform.next_due}` : state.sub}
        days={uniform.days_remaining}
      />
    </div>
  );
}

// ── Restricted (R→S) Progress ──────────────────────────────────────────────

export function RestrictedProgressCard({ instructorId, rToS, onChanged }) {
  const [saving, setSaving] = useState(false);
  if (!rToS || !rToS.applicable) return null;

  const setReport = async (approved) => {
    setSaving(true);
    try {
      await axios.post(`${API}/di/instructors/${instructorId}/supervisor-report`, { approved }, { withCredentials: true });
      toast.success(approved ? "Relatório aprovado" : "Relatório revogado");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 rounded border border-amber-500/40 bg-amber-500/5 space-y-3" data-testid="di-r-to-s-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500/30 text-amber-200 border border-amber-500/50">R</span>
          <span className="text-sm font-semibold text-amber-200">Progresso para perder restrição</span>
        </div>
        {rToS.all_ok && <span className="text-[11px] text-emerald-300 font-semibold">✓ Todos os critérios cumpridos — pronto para promover</span>}
      </div>
      <div className="space-y-3">
        <ProgressBar label="Horas de instrução" value={rToS.instr_min ?? 0} target={rToS.required_instr_min ?? 50 * 60} ok={rToS.hours_ok} mode="minutes" />
        <ProgressBar label="Voos solo (supervisionados + largada)" value={rToS.solos_count} target={rToS.required_solos} ok={rToS.solos_ok} />
      </div>
      <div className="flex items-center justify-between gap-2 p-2 rounded border border-slate-700 bg-slate-900/40">
        <div className="text-xs">
          <div className="font-semibold text-slate-200">Relatório do supervisor{rToS.report_ok && <span className="ml-2 text-emerald-300">✓ Aprovado</span>}</div>
          {rToS.report_approved_at && <div className="text-[10px] text-slate-500">em {fmtDate(rToS.report_approved_at)}</div>}
          {!rToS.report_ok && !(rToS.hours_ok && rToS.solos_ok) && <div className="text-[10px] text-amber-400 mt-0.5">Disponível depois de cumprir {minutesToHHmmInput(rToS.required_instr_min ?? 50 * 60)} instrução + {rToS.required_solos} voos solo</div>}
        </div>
        {rToS.report_ok ? (
          <Button size="sm" variant="outline" disabled={saving} onClick={() => setReport(false)} data-testid="di-supervisor-report-revoke">Revogar</Button>
        ) : (
          <Button size="sm" className="btn-cockpit" disabled={saving || !(rToS.hours_ok && rToS.solos_ok)} onClick={() => setReport(true)} data-testid="di-supervisor-report-approve" title={!(rToS.hours_ok && rToS.solos_ok) ? "Aprovação só fica disponível depois de cumpridos os critérios de horas e voos solo." : ""}>
            Aprovar relatório
          </Button>
        )}
      </div>
    </div>
  );
}

// ── IOT (Instrutor de Instrutores) ─────────────────────────────────────────

function IotCriterion({ ok, label, value }) {
  return (
    <div className={`px-2.5 py-1.5 rounded border text-[11px] flex items-center gap-2 ${ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid="di-iot-criterion">
      {ok ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-200 truncate">{label}</div>
        <div className="text-slate-400 truncate">{value}</div>
      </div>
    </div>
  );
}

function IotExaminerTestForm({ instructorId, currentTest, currentValidUntil, onClose, onSaved }) {
  const [form, setForm] = useState({ passed_at: currentTest?.passed_at || new Date().toISOString().slice(0, 10), examiner_id: currentTest?.examiner_id || "", anac_ref: currentTest?.anac_ref || "", document_url: currentTest?.document_url || "", valid_until: currentValidUntil || "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!form.passed_at) { setError("A data da prova é obrigatória."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/di/instructors/${instructorId}/examiner-test`, { passed_at: form.passed_at, examiner_id: form.examiner_id || null, anac_ref: form.anac_ref || null, document_url: form.document_url || null, valid_until: form.valid_until || null }, { withCredentials: true });
      onSaved();
    } catch (err) { setError(err?.response?.data?.detail || "Falha ao registar."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-md" data-testid="di-iot-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Registar prova prática ANAC</DialogTitle>
          <DialogDescription className="text-slate-400">Necessária para qualificar o instrutor para formar outros instrutores.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-xs">
          <div><Label className="text-slate-300">Data da aprovação *</Label><Input type="date" value={form.passed_at} onChange={(e) => setForm({ ...form, passed_at: e.target.value })} data-testid="di-iot-passed-at" className="bg-slate-800 border-slate-700" /></div>
          <div><Label className="text-slate-300">Validade da qualificação</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="di-iot-valid-until" className="bg-slate-800 border-slate-700" /></div>
          <div><Label className="text-slate-300">Referência ANAC</Label><Input value={form.anac_ref} onChange={(e) => setForm({ ...form, anac_ref: e.target.value })} placeholder="Ex: ANAC/2025/123" data-testid="di-iot-anac-ref" className="bg-slate-800 border-slate-700" /></div>
          <div><Label className="text-slate-300">Examinador (ID ou nome)</Label><Input value={form.examiner_id} onChange={(e) => setForm({ ...form, examiner_id: e.target.value })} data-testid="di-iot-examiner" className="bg-slate-800 border-slate-700" /></div>
          <div><Label className="text-slate-300">URL do documento</Label><Input value={form.document_url} onChange={(e) => setForm({ ...form, document_url: e.target.value })} placeholder="https://..." data-testid="di-iot-document" className="bg-slate-800 border-slate-700" /></div>
          {error && <div className="text-red-400 text-[11px]">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="di-iot-submit">{saving ? "A guardar…" : "Guardar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function IotCard({ instructorId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/di/instructors/${instructorId}/can-train-instructors`, { withCredentials: true });
      setData(r.data);
    } catch (err) { console.warn("[iot-card] load failed:", err?.message || err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [instructorId]);

  if (loading || !data) return null;
  const elig = data.eligibility || {};
  const c = elig.criteria || {};
  const qual = data.qualification || {};
  const borderCls = data.can_train ? "border-emerald-500/30 bg-emerald-500/5" : qual.present ? "border-amber-500/30 bg-amber-500/5" : "border-slate-700 bg-slate-800/30";

  return (
    <div className={`p-3 rounded border text-xs ${borderCls}`} data-testid="di-iot-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          {data.can_train ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
          Instrutor de Instrutores
          {data.can_train && <span className="ml-1 inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-200 bg-emerald-500/15" data-testid="di-iot-badge">Apto</span>}
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowForm(true)} data-testid="di-iot-register-test-btn" className="h-7 text-[11px]">
          {qual.present && c.anac_test?.ok ? "Atualizar prova" : "Registar prova ANAC"}
        </Button>
      </div>
      {!data.can_train && data.reason && <div className="text-slate-300 mt-1.5">{data.reason}</div>}
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1.5">
        <IotCriterion ok={c.years_as_instructor?.ok} label="≥2 anos como instrutor" value={c.years_as_instructor?.value != null ? `${c.years_as_instructor.value} anos` : "Sem data de emissão"} />
        <IotCriterion ok={c.total_hours?.ok} label="≥500h voo total" value={`${c.total_hours?.value_h ?? 0}h de 500h${c.total_hours?.missing_h > 0 ? ` (faltam ${c.total_hours.missing_h}h)` : ""}`} />
        <IotCriterion ok={c.instructor_hours?.ok} label="≥200h instrução" value={`${c.instructor_hours?.value_h ?? 0}h de 200h${c.instructor_hours?.missing_h > 0 ? ` (faltam ${c.instructor_hours.missing_h}h)` : ""}`} />
        <IotCriterion ok={c.anac_test?.ok} label="Prova prática ANAC" value={c.anac_test?.ok ? `Aprovada ${c.anac_test.passed_at}${c.anac_test.anac_ref ? ` · ${c.anac_test.anac_ref}` : ""}` : "Não registada"} />
      </div>
      {qual.present && qual.valid_until && (
        <div className={`mt-2 text-[11px] ${qual.expired ? "text-red-300" : qual.days_remaining <= 45 ? "text-amber-300" : "text-slate-400"}`}>
          Validade da qualificação: <span className="font-mono">{qual.valid_until}</span>
          {qual.days_remaining != null && <span className="ml-1">({qual.expired ? `expirou há ${-qual.days_remaining}d` : `${qual.days_remaining}d restantes`})</span>}
        </div>
      )}
      {showForm && <IotExaminerTestForm instructorId={instructorId} currentTest={c.anac_test?.ok ? c.anac_test : null} currentValidUntil={qual.valid_until} onClose={() => setShowForm(false)} onSaved={async () => { setShowForm(false); await load(); if (onChanged) onChanged(); }} />}
    </div>
  );
}

// ── Status Overview ────────────────────────────────────────────────────────

export default function StatusOverview({ detail, asOf, setAsOf, onApply, onChanged }) {
  const status = detail.status || "red";
  const meta = STATUS_META[status];
  const apto = detail.apto || {};
  const reval = detail.revalidacao || {};
  const hours = detail.hours || {};
  const qualifications = apto.criteria?.qualifications?.all ?? [];

  const sectionCls = "rounded border border-slate-700/60 bg-slate-900/40";
  const sectionSummaryCls = "cursor-pointer select-none px-3 py-2 text-xs font-semibold text-slate-200 flex items-center justify-between gap-2";

  return (
    <div className="space-y-3">
      <div className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded ${meta.bg} ${meta.border} border`}>
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${meta.dot} animate-pulse`} />
          <div>
            <div className={`text-sm font-bold ${meta.color}`} data-testid="di-status-label">{meta.label}</div>
            <div className="text-[11px] text-slate-400">Cálculo a {hours.as_of || "hoje"}</div>
            {status === "amber" && (() => {
              const reasons = [];
              const c = apto.criteria || {};
              if (c.hours_15h_12m?.ok && c.hours_15h_12m?.value_h < 18) reasons.push(`Horas próximas do mínimo ANAC (${minutesToHHmmInput((c.hours_15h_12m.value_min || 0) - 900)} acima de 15:00)`);
              for (const { key, label } of [{ key: "medical", label: "Certificado médico" }, { key: "instructor_license", label: "Licença de instrutor" }, { key: "pu_license", label: "Licença PU" }]) {
                const d = c[key]?.days_remaining;
                if (d != null && d >= 0 && d <= 45) reasons.push(`${label} expira em ${d} dia(s)`);
              }
              return reasons.length > 0 ? <div className="text-[10px] text-amber-300/90 mt-0.5">{reasons.map((r, i) => <div key={`amber-reason-${i}-${r.slice(0,10)}`}>⚠ {r}</div>)}</div> : null;
            })()}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <Input type="date" value={asOf || todayStr()} onChange={(e) => { const v = e.target.value; setAsOf(v === todayStr() ? "" : v); }} className="w-40 h-8 text-xs" data-testid="di-asof-date" title="Data de cálculo (hoje por defeito)" />
          <Button size="sm" variant="outline" onClick={onApply} data-testid="di-asof-apply">Simular</Button>
          {asOf && <Button size="sm" variant="ghost" onClick={() => { setAsOf(""); setTimeout(onApply, 0); }}>Hoje</Button>}
        </div>
      </div>

      <RestrictedProgressCard instructorId={detail.id} rToS={detail.r_to_s} onChanged={onChanged} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <CritTile ok={apto.criteria?.hours_15h_12m?.ok} label="≥15h voadas / 12m" sub={`${minutesToHHmmInput(apto.criteria?.hours_15h_12m?.value_min ?? 0)} de 15:00${apto.criteria?.hours_15h_12m?.missing_min ? ` (faltam ${minutesToHHmmInput(apto.criteria.hours_15h_12m.missing_min)})` : (apto.criteria?.hours_15h_12m?.ok && apto.criteria.hours_15h_12m.value_h < 18 ? ` (margem: ${minutesToHHmmInput((apto.criteria.hours_15h_12m.value_min || 0) - 900)})` : "")}`} warn={apto.criteria?.hours_15h_12m?.ok && apto.criteria?.hours_15h_12m?.value_h < 18} />
        <CritTile ok={apto.criteria?.medical?.ok} label="Cert. médico" sub={fmtDate(apto.criteria?.medical?.valid_until)} days={apto.criteria?.medical?.days_remaining} />
        <CritTile ok={apto.criteria?.pu_license?.ok} label="Licença PU" sub={fmtDate(apto.criteria?.pu_license?.valid_until)} days={apto.criteria?.pu_license?.days_remaining} />
        <CritTile ok={apto.criteria?.instructor_license?.ok} label="Licença instrutor" sub={fmtDate(apto.criteria?.instructor_license?.valid_until)} days={apto.criteria?.instructor_license?.days_remaining} />
        <CritTile
          ok
          warn={!reval.automatic_revalidation}
          label="Revalidação licença instrutor"
          sub={<>
            {minutesToHHmmInput(reval.value_min ?? 0)} instrução / {minutesToHHmmInput(reval.required_min ?? 25 * 60)}
            {reval.missing_min ? ` · faltam ${minutesToHHmmInput(reval.missing_min)}` : ""}
            {reval.next_action && <div className="text-slate-400 mt-0.5">{reval.next_action}</div>}
          </>}
        />
        <UniformizacaoCard uniform={detail.uniformizacao} />
        {qualifications.length === 0 ? (
          <div className="col-span-2 md:col-span-4 px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-xs text-red-300">Sem qualificações registadas. Para dar instrução, é obrigatório ≥1 qualificação válida.</div>
        ) : qualifications.map((q, idx) => (
          <CritTile key={`${q.type}-${idx}`} ok={q.ok} label={QUAL_LABELS[q.type] || q.type} sub={q.valid_until ? `Válida até ${fmtDate(q.valid_until)}` : (q.ok ? "Sem prazo" : (q.reason || "Inválida"))} days={q.days_remaining} />
        ))}
      </div>

      <details className={sectionCls} data-testid="di-section-hours">
        <summary className={sectionSummaryCls}>
          Horas
          <span className="text-[11px] font-normal text-slate-400">total e últimos 12 meses</span>
        </summary>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-3 pb-3">
          <SmallStat icon={<Plane />} label="Total horas" value={minutesToHHmmInput(hours.total_min ?? 0)} tooltip={(hours.initial_pic_min || 0) > 0 ? `Iniciais ${minutesToHHmmInput(hours.initial_pic_min)} + Voadas na app ${minutesToHHmmInput(hours.logged_total_min ?? 0)}` : undefined} />
          <SmallStat icon={<Activity />} label="Horas instrução" value={minutesToHHmmInput(hours.instr_min ?? 0)} tooltip={(hours.initial_instructor_min || 0) > 0 ? `Iniciais ${minutesToHHmmInput(hours.initial_instructor_min)} + Voadas na app ${minutesToHHmmInput(hours.logged_instr_min ?? 0)}` : undefined} />
          <SmallStat icon={<Clock />} label="Últimos 12m (PIC)" value={minutesToHHmmInput(hours.last12m_total_min ?? 0)} accent={hours.last12m_total_h >= 15 ? "ok" : "bad"} />
          <SmallStat icon={<Clock />} label="Últimos 12m (instr)" value={minutesToHHmmInput(hours.last12m_instr_min ?? 0)} accent={hours.last12m_instr_h >= 25 ? "ok" : "warn"} />
        </div>
      </details>

      <details className={sectionCls} data-testid="di-section-examiner">
        <summary className={sectionSummaryCls}>
          Instrutor de Instrutores
          <span className="text-[11px] font-normal text-slate-400">prova prática e critérios</span>
        </summary>
        <div className="px-3 pb-3">
          <IotCard instructorId={detail.id} onChanged={onChanged} />
        </div>
      </details>
    </div>
  );
}
